import { createGlobalState } from "@vueuse/core";
import { computed, readonly, ref } from "vue";
import { getSearchResults } from "@/core/api/graphql/catalog";
import { useThemeContext } from "@/core/composables";
import { Logger } from "@/core/utilities";
import { highlightSearchText, prepareSearchText } from "../utils";
import type { GetSearchResultsParamsType } from "@/core/api/graphql/catalog";
import type { Category, PageType, Product } from "@/core/api/graphql/types";

interface ISearchResultsConnectionType<TItem> {
  items?: TItem[];
  totalCount?: number;
}

/**
 * Extracts the display name from a page object.
 * Handles both PageType (with 'name') and PageDocumentType (with 'title').
 * Falls back to permalink if neither name nor title is available.
 */
function getPageName(item: { title?: string; name?: string; permalink?: string }) {
  return item.name ?? item.title ?? item.permalink ?? "";
}

function _useSearchBar() {
  const { themeContext } = useThemeContext();

  const loading = ref(false);
  const searchBarVisible = ref(false);
  const searchDropdownVisible = ref(false);
  const searchPhraseOfUploadedResults = ref("");
  const categories = ref<Category[]>([]);
  // Innovadis custom: keep article groups and variations separate for dropdown rendering.
  const articleGroups = ref<Product[]>([]);
  const variations = ref<Product[]>([]);

  const products = ref<Product[]>([]);
  const pages = ref<PageType[]>([]);
  const suggestions = ref<{ text: string; label: string }[]>([]);
  const total = ref(0);

  const maxSearchLength = computed(() => themeContext.value?.settings?.search_max_chars || 999);

  function showSearchDropdown() {
    if (!searchDropdownVisible.value) {
      searchDropdownVisible.value = true;
    }
  }

  function hideSearchDropdown() {
    if (searchDropdownVisible.value) {
      searchDropdownVisible.value = false;
    }
  }

  function toggleSearchBar() {
    searchBarVisible.value = !searchBarVisible.value;
  }

  function hideSearchBar() {
    if (searchBarVisible.value) {
      searchBarVisible.value = false;
    }
  }

  async function searchResults(params: GetSearchResultsParamsType) {
    const preparedParams: GetSearchResultsParamsType = {
      ...params,
      keyword: prepareSearchText(params.keyword),
    };

    loading.value = true;

    try {
      const result = await getSearchResults(preparedParams);
      if (!result) {
        return;
      }

      // Innovadis custom: describe the aliased search sections until GraphQL codegen is refreshed.
      const typedResult = result as {
        productSuggestions?: { suggestions?: string[] };
        pages?: { items?: PageType[] };
        categories?: { items?: Category[] };
        articleGroups?: ISearchResultsConnectionType<Product>;
        variations?: ISearchResultsConnectionType<Product>;
      };

      const suggestionsItems = typedResult.productSuggestions?.suggestions ?? [];
      const pagesItems = typedResult.pages?.items ?? [];
      const categoriesItems = typedResult.categories?.items ?? [];
      const articleGroupsItems = typedResult.articleGroups?.items ?? [];
      const articleGroupsTotalCount = typedResult.articleGroups?.totalCount ?? 0;
      const variationsItems = typedResult.variations?.items ?? [];
      const variationsTotalCount = typedResult.variations?.totalCount ?? 0;

      // Innovadis custom: remove brand content pages from search result (this content is injected into virto brand pages and are not to be shown on their own)
      const excludes = ["/merk-content", "/brand-content", "/markeninhalte"];

      // Innovadis custom: separate brand content pages from search result
      const [filteredPages, brandPages] = pagesItems.reduce<[PageType[], PageType[]]>(
        ([normal, brand], item) => {
          const permalink = item.permalink ?? "";
          const typedItem = item as PageType;

          if (excludes.some((exclude) => permalink.startsWith(exclude))) {
            return [normal, [...brand, typedItem]];
          } else {
            return [[...normal, typedItem], brand];
          }
        },
        [[], []],
      );

      pages.value = filteredPages.map((item) => ({
        ...item,
        name: highlightSearchText(getPageName(item), params.keyword),
      })) as PageType[]; // TODO: remove type assertion

      // Innovadis custom: TODO handle brand content
      const brandContentPages = brandPages.map((item) => ({
        ...item,
        name: highlightSearchText(item.name ?? "", params.keyword),
      })) as PageType[]; // TODO: remove type assertion
      if (brandContentPages) {
        // Either get virto to include brand pages into search result
        // Or map brandContentPages to virto brands
        //  /merk-content/cirteq -->
        //  /brands/cirteq (if it exists)
      }

      categories.value = categoriesItems.map((item) => ({
        ...item,
        name: highlightSearchText(item.name, params.keyword),
      })) as Category[]; // TODO: remove type assertion

      total.value = articleGroupsTotalCount + variationsTotalCount;
      articleGroups.value = articleGroupsItems as Product[]; // TODO: remove type assertion
      variations.value = variationsItems as Product[]; // TODO: remove type assertion
      // Innovadis custom: keep `products` populated with the combined article groups + variations
      // so components that haven't been migrated to the split `articleGroups`/`variations` refs
      // (search-dropdown.vue, inno-search-bar.vue) keep working.
      products.value = [...articleGroupsItems, ...variationsItems] as Product[]; // TODO: remove type assertion
      searchPhraseOfUploadedResults.value = preparedParams.keyword;
      loading.value = false;
      loading.value = false;
    } catch (e) {
      Logger.error(`${useSearchBar.name}.${searchResults.name}`, e);
      loading.value = false;
      throw e;
    }
  }

  function clearSearchResults() {
    suggestions.value = [];
    pages.value = [];
    categories.value = [];
    total.value = 0;
    products.value = [];
    searchPhraseOfUploadedResults.value = "";
  }

  return {
    searchResults,
    toggleSearchBar,
    hideSearchBar,
    showSearchDropdown,
    hideSearchDropdown,
    total: readonly(total),
    loading: readonly(loading),
    searchBarVisible: readonly(searchBarVisible),
    searchDropdownVisible: readonly(searchDropdownVisible),
    searchPhraseOfUploadedResults: readonly(searchPhraseOfUploadedResults),
    maxSearchLength,
    categories: computed(() => categories.value),
    products: computed(() => products.value),
    pages: computed(() => pages.value),
    suggestions: computed(() => suggestions.value),

    clearSearchResults,
  };
}

export const useSearchBar = createGlobalState(_useSearchBar);
